// A rule's published wording (TGCLOUD-1910), exactly as the rule pack holds it:
// the words leading into it (its section's lead, then each enclosing item), then
// the rule's own item. Never translated and never reworded: NICE's licence
// requires its recommendations verbatim, and the other packs' wording is the
// publisher's too (triageText.js says why). "Show in full" reads the whole
// section from the pack, at the version the result came from.
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The section, item by item, with the rule's own item in bold.
function SectionText({ section, path }) {
  const renderItems = (items, depth, prefix) => (
    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
      {items.map((item, i) => {
        const at = [...prefix, i];
        const own =
          Array.isArray(path) &&
          path.length === at.length &&
          path.every((p, k) => p === at[k]);
        return (
          <Box component="li" key={i} sx={{ mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: own ? 700 : 400, whiteSpace: 'pre-wrap' }}
            >
              {item.text}
            </Typography>
            {Array.isArray(item.items) &&
              item.items.length > 0 &&
              renderItems(item.items, depth + 1, at)}
          </Box>
        );
      })}
    </Box>
  );
  return (
    <Box>
      {section.heading && (
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          {section.heading}
        </Typography>
      )}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {section.id} {section.title}
      </Typography>
      {section.text?.lead && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {section.text.lead}
        </Typography>
      )}
      {Array.isArray(section.text?.items) &&
        renderItems(section.text.items, 0, [])}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {object} props.rule      a fired / near-miss / not-evaluated entry
 * @param {string} props.packId
 * @param {string} props.packVersion
 * @param {boolean} [props.allowFull=true] offer "Show in full"
 */
export default function RuleWording({
  rule,
  packId,
  packVersion,
  allowFull = true
}) {
  const t = useAutoTranslation();
  const [open, setOpen] = React.useState(false);
  const [section, setSection] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  const showFull = () => {
    setOpen(true);
    if (section) return;
    setFailed(false);
    axios
      .get(
        APICONSTANTS.triagePack
          .replace(':packId', encodeURIComponent(packId))
          .replace(':version', encodeURIComponent(packVersion))
      )
      .then(({ data }) => {
        const found = (data?.sections || []).find((s) => s.id === rule.section);
        if (found) setSection(found);
        else setFailed(true);
      })
      .catch(() => setFailed(true));
  };

  const context = Array.isArray(rule.context) ? rule.context : [];
  return (
    <Box data-testid="triage-rule-wording">
      {context.map((line, i) => (
        <Typography
          key={i}
          variant="body2"
          sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
        >
          {line}
        </Typography>
      ))}
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', pl: context.length ? 1.5 : 0 }}
      >
        {rule.text}
      </Typography>
      {allowFull && rule.section && packId && packVersion && (
        <Button
          size="small"
          onClick={showFull}
          sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
        >
          {t('Show {{section}} in full', { section: rule.section })}
        </Button>
      )}
      {open && (
        <CustomDialog
          open
          title="Published wording"
          handleClose={() => setOpen(false)}
          allowBackgroundClose
          maxWidth="md"
          minHeight="auto"
          onClose
          showCancelBtn
          saveBtnLabel="Close"
        >
          {section ? (
            <SectionText section={section} path={rule.path} />
          ) : failed ? (
            <Typography variant="body2">
              {t('The published wording could not be loaded.')}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('Loading…')}
            </Typography>
          )}
        </CustomDialog>
      )}
    </Box>
  );
}
